import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { schedule } from "@ember/runloop";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { bbcodeAttributeEncode } from "discourse/lib/bbcode-attributes";
import { generateIcsData } from "discourse/lib/download-calendar";
import discourseLater from "discourse/lib/later";
import { applyLocalDates } from "discourse/lib/local-dates";
import { cook } from "discourse/lib/text";
import dIcon from "discourse/ui-kit/helpers/d-icon";

export default class DiscoursePostEventDates extends Component {
  @service siteSettings;
  @service currentUser;

  @tracked htmlDates = "";

  get timezone() {
    return this.args.event.timezone || "UTC";
  }

  get showLocalTime() {
    return this.args.event.showLocalTime ?? true;
  }

  get startsAt() {
    return moment.tz(this.args.event.startsAt, this.timezone);
  }

  get endsAt() {
    const eventEndsAt = this.args.event.endsAt;
    return eventEndsAt ? moment.tz(eventEndsAt, this.timezone) : null;
  }

  get startsAtFormat() {
    const includeYear = !this.isSameYear(this.startsAt);
    const includeTime = this.hasTime(this.startsAt) || this.isSingleDayEvent;
    return this._buildFormat(this.startsAt, { includeYear, includeTime });
  }

  get endsAtFormat() {
    if (this.isSingleDayEvent) {
      return "LT";
    }

    const endsAt = this.endsAt;
    const includeYear =
      !this.isSameYear(endsAt) || !this.isSameYear(endsAt, this.startsAt);
    const includeTime = this.hasTime(endsAt);

    return this._buildFormat(endsAt, { includeYear, includeTime });
  }

  get isSingleDayEvent() {
    return this.startsAt.isSame(this.endsAt, "day");
  }

  get datesBBCode() {
    const dates = [];

    const startDate = this.buildDateBBCode({
      date: this.startsAt,
      format: this.startsAtFormat,
      range: !!this.endsAt && "from",
    });

    dates.push(startDate);

    if (this.endsAt) {
      const endDate = this.buildDateBBCode({
        date: this.endsAt,
        format: this.endsAtFormat,
        range: "to",
      });
      dates.push(endDate);
    }

    return dates;
  }

  isSameYear(date1, date2) {
    return date1.isSame(date2 || moment(), "year");
  }

  hasTime(date) {
    return date.hour() || date.minute();
  }

  generateIcsForEvent() {
    const event = this.args.event;
    if (!event || !this.startsAt) {
      return null;
    }

    const title = event.name || event.post?.topic?.title || "Event";
    const startsAt = this.startsAt.toISOString();
    const endsAt = this.endsAt
      ? this.endsAt.toISOString()
      : moment(this.startsAt).add(1, "hours").toISOString();

    const dates = [{ startsAt, endsAt, timezone: this.timezone }];
    const options = {};

    if (event.rrule) {
      options.rrule = event.rrule;
    }

    if (event.location) {
      options.location = event.location;
    }

    if (event.description) {
      options.details = event.description;
    }

    return generateIcsData(title, dates, options);
  }

  buildDateBBCode({ date, format, range }) {
    const bbcode = {
      date: date.format("YYYY-MM-DD"),
      time: date.format("HH:mm"),
      format,
      timezone: date.tz(),
      postId: this.args.event.id,
      calendar: "on",
    };

    if (this.args.event.showLocalTime) {
      bbcode.displayedTimezone = this.args.event.timezone;
    }

    if (range) {
      bbcode.range = range;
    }

    const icsData = this.generateIcsForEvent();
    if (icsData) {
      bbcode.ics = bbcodeAttributeEncode(icsData);
    }

    const content = Object.entries(bbcode)
      .map(([key, value]) => `${key}=${value}`)
      .join(" ");

    return `[${content}]`;
  }

  @action
  async computeDates(element) {
    if (this.args.event.allDay) {
      const start = moment(this.args.event.startsAt, "YYYY-MM-DD");
      const includeStartYear = !start.isSame(moment(), "year");
      const startFormat = includeStartYear ? "ddd, MMM D, YYYY" : "ddd, MMM D";
      let dates = start.format(startFormat);

      if (this.args.event.endsAt) {
        const end = moment(this.args.event.endsAt, "YYYY-MM-DD");
        if (!end.isSame(start, "day")) {
          const includeEndYear =
            !end.isSame(moment(), "year") || !end.isSame(start, "year");
          const endFormat = includeEndYear ? "ddd, MMM D, YYYY" : "ddd, MMM D";
          dates += ` → ${end.format(endFormat)}`;
        }
      }

      this.htmlDates = trustHTML(dates);
      return;
    }

    if (this.siteSettings.discourse_local_dates_enabled) {
      const bbcode = this.datesBBCode.join("<span> → </span>");
      const result = await cook(bbcode);
      this.htmlDates = trustHTML(result.toString());

      // doesn’t work reliably without discourseLater
      discourseLater(() => {
        schedule("afterRender", () => {
          if (this.isDestroying) {
            return;
          }

          const localDateElements = element.querySelectorAll(
            `[data-post-id="${this.args.event.id}"].discourse-local-date`
          );
          applyLocalDates(
            localDateElements,
            this.siteSettings,
            this.currentUser?.user_option?.timezone
          );
        });
      });
    } else {
      let dates = `${this.startsAt.format(this.startsAtFormat)}`;
      if (this.endsAt) {
        const endFormatted = moment(this.endsAt).format(this.endsAtFormat);
        dates += ` → ${endFormatted}`;
      }
      this.htmlDates = trustHTML(dates);
    }
  }

  _buildFormat(date, { includeYear, includeTime }) {
    const formatParts = ["ddd, MMM D"];
    if (includeYear) {
      formatParts.push("YYYY");
    }

    const dateString = formatParts.join(", ");
    const timeString = includeTime ? " LT" : "";

    return `\u0022${dateString}${timeString}\u0022`;
  }

  <template>
    <section
      class="event__section event-dates"
      data-event-id={{@event.id}}
      {{didInsert this.computeDates}}
    >
      {{dIcon "clock"}}
      {{this.htmlDates}}
    </section>
  </template>
}
